import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import styles from "./EngagementApproverSetting.module.css";
import { EngagementApprover, EngagementApproverSettingProps } from "./EngagementApproverSetting.utils";

/**
 * EngagementApproverSetting
 *
 * Displays the UI to select and manage approvers from a user list.
 * - Supports adding approvers via a dropdown.
 * - Prevents duplicates.
 * - Renders approver cards with basic info and removal option.
 */
const EngagementApproverSetting: React.FC<EngagementApproverSettingProps> = ({
  allUsers,
  selectedApprovers,
  setSelectedApprovers,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties | null>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);

  const availableUsers = allUsers.filter(
    (user) => !selectedApprovers.some((a) => a.Email === user.Email)
  );

  const handleAddApprover = (user: EngagementApprover) => {
    setSelectedApprovers([...selectedApprovers, user]);
    setShowDropdown(false);
  };

  const openDropdown = () => {
    if (addBtnRef.current) {
      const rect = addBtnRef.current.getBoundingClientRect();
      setDropdownStyles({
        position: "fixed",
        top: rect.bottom + 5,
        left: rect.left,
        width: 300,
        zIndex: 9999,
        background: "#fff",
        borderRadius: 5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        maxHeight: 300,
        overflowY: "auto",
      });
      setShowDropdown(true);
    }
  };

  const closeDropdown = () => setShowDropdown(false);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const dropdownEl = document.getElementById("approver-custom-dropdown");
      if (
        showDropdown &&
        !dropdownEl?.contains(e.target as Node) &&
        !addBtnRef.current?.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showDropdown]);

  return (
    <>
      <div className={styles.approverHeaderRow}>
        <div className={styles.headerTitle}>Approvers</div>
        <div>
          <Button
            className={styles.addBtn}
            ref={addBtnRef}
            onClick={openDropdown}
            disabled={availableUsers.length === 0}
          >
            <Icon.Plus className={styles.addBtnIcon} /> Add approver
          </Button>
        </div>
      </div>

      {showDropdown &&
        dropdownStyles &&
        ReactDOM.createPortal(
          <div id="approver-custom-dropdown" style={dropdownStyles}>
            {availableUsers.length > 0 ? (
              availableUsers.map((user) => (
                <div
                  key={user.Email}
                  onClick={() => handleAddApprover(user)}
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                  onMouseEnter={(e) =>
                    ((e.target as HTMLDivElement).style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    ((e.target as HTMLDivElement).style.backgroundColor = "#fff")
                  }
                >
                  {user.FirstName} {user.LastName} - {user.Role}
                </div>
              ))
            ) : (
              <div style={{ padding: "8px 12px", color: "#777" }}>No approvers available</div>
            )}
          </div>,
          document.body
        )}

      {selectedApprovers.length === 0 && (
        <p className="text-danger">No approvers present in the document.</p>
      )}

      <div className={styles.approverList}>
        {selectedApprovers.map((approver) => (
          <div key={approver.Email} className={styles.approverCard}>
            <div className={styles.avatarCircle}>
              {`${approver.FirstName?.[0] ?? ""}${approver.LastName?.[0] ?? ""}`.toUpperCase()}
            </div>
            <div className={styles.approverDetails}>
              <div className={styles.approverName}>
                {approver.FirstName} {approver.LastName}
              </div>
              <div className={styles.meta}>
                <div className={styles.companyIcon}>
                  {approver.FirmName?.split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 3)
                    .toUpperCase()}
                </div>
                {approver.Role} {approver.FirmName ? " - " : ""} {approver.FirmName}
              </div>
              <div className={styles.emailHolder}>
                <a href={`mailto:${approver.Email}`} className={styles.email}>
                  {approver.Email}
                </a>
              </div>
            </div>
            <Icon.Trash
              className={styles.deleteIcon}
              onClick={() =>
                setSelectedApprovers(
                  selectedApprovers.filter((a) => a.Email !== approver.Email)
                )
              }
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default EngagementApproverSetting;
